/**
 * lib/api.ts — the only place this client speaks to the server.
 *
 * Three server-side rules make a hand-rolled `fetch` at each call site a liability, and each one is
 * enforced here exactly once:
 *
 * **1. The business unit travels on every request, not just on writes.** `server/app.ts` requires a
 * mutating request to NAME its business unit — `X-Ticketless-BU` or a `buId` field — and refuses when the
 * name disagrees with the Decision it resolved. That guard exists because of a confirmed cross-tenant leak
 * in a sibling application: two tabs open on two business units, a write issued from tab B while the
 * server's notion of "current" was tab A's, and the row landed in the wrong tenant with every check
 * passing. The header is therefore sent on READS too, though the server would tolerate its absence. A read
 * that omits it is answered from whichever scope the server picked, which is how a stale tab comes to show
 * one tenant's rows under another tenant's heading — and the user then acts on what they were shown.
 *
 * **2. CSRF is double-submit.** The `tl_csrf` cookie is deliberately readable by script (that is the whole
 * mechanism) and its value must be copied into `X-CSRF-Token` on every unsafe verb. The cookie is the
 * source of truth rather than a value cached from `/api/me`: a session bump re-mints the token, and a page
 * that kept the old one in memory would fail every write until it reloaded.
 *
 * **3. A refusal is JSON with a stable `error` code, never a message to be shown raw.** `problemFor` keeps
 * internal detail out of the body on purpose, so `ApiError` carries the code, the status and the trace id,
 * and the UI decides the sentence. Rendering `message` verbatim would show the user "You do not have
 * access to this." where the screen already knows how to say something useful.
 *
 * A 401 is handled here and only here: the session is gone, every in-flight screen is now wrong, and the
 * one correct response is to send the browser to the sign-in view. The handler is injectable so a test can
 * observe it rather than navigate.
 */

/** The header `server/app.ts` reads first when deciding which business unit a request names. */
export const BU_HEADER = "X-Ticketless-BU";

/** The readable half of the double submit, and the header its value must be copied into. */
export const CSRF_COOKIE = "tl_csrf";
export const CSRF_HEADER = "X-CSRF-Token";

/** Verbs that change nothing, as an allowlist — the same set the server exempts from CSRF. */
const SAFE_METHODS: ReadonlySet<string> = new Set(["GET", "HEAD", "OPTIONS"]);

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

/**
 * The wire shape of every refusal and failure. `error` is switched on; `message` is never shown raw —
 * **except for the goals routes**, which are the one deliberate exception in this application. See
 * `GOAL_REFUSAL_CODES` and `goalErrorMessage` at the foot of this file.
 */
export interface Problem {
  readonly error: string;
  readonly message: string;
  readonly traceId: string;
  /**
   * Whether offering "try again" is honest — sent only by `server/goals/refusals.ts`, absent everywhere
   * else, and therefore `null` rather than `false` when it is missing. The three values are three
   * different facts: `true` "nothing landed, retry", `false` "do NOT retry", `null` "this route does not
   * say". Defaulting a missing field to `true` would invite a retry on every ordinary 500.
   */
  readonly retryable: boolean | null;
}

/**
 * One typed failure the UI can render.
 *
 * `code` is the server's vocabulary (`scope_denied`, `not_admitted`, `csrf_failed`, `invalid_request`, …)
 * and is safe to branch on; `traceId` is what an operator needs to find the corresponding log line, so it
 * is surfaced in the error state rather than swallowed.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string | null;
  /**
   * The server's own verdict on whether a retry is honest, or `null` where the route did not say.
   *
   * ⚠️ **`false` is not "unknown" and must never be treated as one.** `checkin_indeterminate` is the
   * refusal this field exists for: a goal update is additive and has no delete, so a second post leaves
   * two contradictory check-ins on a record somebody's leadership reads. A screen that offered a retry
   * because it had no opinion would be doing exactly the thing the server declined to invite.
   */
  readonly retryable: boolean | null;

  constructor(
    status: number,
    code: string,
    message: string,
    traceId: string | null,
    retryable: boolean | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.traceId = traceId;
    this.retryable = retryable;
  }

  /** True where the answer is "we know who you are and the answer is still no" — a distinct empty state. */
  get isPermissionDenied(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** A plain sentence for a state component. Deliberately ours, not the server's. */
  get userMessage(): string {
    switch (this.code) {
      case "not_authenticated":
        return "Your session has ended. Sign in to continue.";
      case "not_admitted":
        return "Your account is awaiting admission by an administrator.";
      case "domain_not_allowed":
        return "This deployment does not accept your email domain.";
      case "scope_denied":
      case "scope_unknown":
        return "You do not have access to this business unit.";
      case "resource_denied":
      case "section_denied":
      case "role_required":
      case "forbidden":
        return "You do not have permission to do this.";
      case "csrf_failed":
        return "The page went stale. Reload and try again.";
      case "invalid_request":
        return "The request was not valid.";
      case "not_found":
        return "That no longer exists, or was never yours to see.";
      case "unavailable":
        return "That feature is not configured in this deployment.";
      case "changelog_unavailable":
        return "Changelog generation is not configured in this deployment.";
      case "network":
        return "The server could not be reached. Check your connection and retry.";
      default:
        return "Something went wrong. Retry, or quote the trace id below.";
    }
  }
}

/**
 * The business unit every request names, held in one place.
 *
 * The alternative — threading it through every call site — is the arrangement that produced the leak this
 * module's header exists to prevent: one call site forgets, and the server answers from a scope the user
 * did not choose. `App` sets it once when `/api/me` resolves and again whenever the switcher moves.
 */
let activeBu: string | null = null;

export function setActiveBu(buId: string | null): void {
  activeBu = buId;
}

export function getActiveBu(): string | null {
  return activeBu;
}

/** What happens when the server says the session is gone. Overridable so a test need not navigate. */
let onUnauthenticated: () => void = () => {
  window.location.hash = "#/login";
};

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

/** Read one cookie by name. The CSRF cookie is base64url, so no decoding is needed — but be exact anyway. */
export function readCookie(name: string, source: string = document.cookie): string | undefined {
  for (const part of source.split(";")) {
    const eq = part.indexOf("=");
    if (eq <= 0) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    return decodeURIComponent(part.slice(eq + 1).trim());
  }
  return undefined;
}

export interface RequestOptions {
  readonly method?: HttpMethod;
  /** Serialised as JSON. Never include `buId` by hand — the header carries it and disagreement is a 403. */
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  /** Overrides the module-level scope for one call. Rarely needed; the switcher is the normal path. */
  readonly buId?: string | null;
  readonly signal?: AbortSignal;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    params.set(key, String(value));
  }
  const suffix = params.toString();
  return suffix === "" ? path : `${path}?${suffix}`;
}

async function readProblem(response: Response): Promise<Problem | null> {
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return null;
  try {
    const parsed: unknown = await response.json();
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    const error = typeof record["error"] === "string" ? record["error"] : null;
    if (error === null) return null;
    return {
      error,
      message: typeof record["message"] === "string" ? record["message"] : "",
      traceId: typeof record["traceId"] === "string" ? record["traceId"] : "",
      // Absent on every route but goals, and absence is preserved rather than defaulted. See `Problem`.
      retryable: typeof record["retryable"] === "boolean" ? record["retryable"] : null,
    };
  } catch {
    return null;
  }
}

/**
 * One request. Returns the parsed body, or `undefined` for a 204 — which several write routes answer with.
 *
 * The generic is the caller's assertion about the response shape and is not validated at runtime. That is
 * a deliberate line: the server owns these shapes, they are covered by its own tests, and a second schema
 * here would be a second definition to drift. What IS checked is everything the client cannot assume — the
 * status, the content type, and whether the body is a problem.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T | undefined> {
  const method = options.method ?? "GET";
  const bu = options.buId === undefined ? activeBu : options.buId;

  const headers = new Headers({ Accept: "application/json" });
  if (bu !== null) headers.set(BU_HEADER, bu);

  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    // A missing cookie is not a reason to skip the header: sending nothing produces the same 403 as
    // sending a stale value, and omitting it silently would make a genuine cookie problem look like a
    // permissions one.
    if (token !== undefined) headers.set(CSRF_HEADER, token);
  }

  if (options.body !== undefined) headers.set("Content-Type", "application/json");

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // The session and CSRF cookies are the entire auth story; without this every request is anonymous.
      credentials: "same-origin",
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // An aborted request is the caller's own doing (a screen unmounted, a query superseded) and must not
    // be dressed up as a server failure the user is asked to retry.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "network", "the server could not be reached", null);
  }

  const traceId = response.headers.get("x-c4s-trace-id");

  if (response.status === 401) {
    onUnauthenticated();
    const problem = await readProblem(response);
    throw new ApiError(401, problem?.error ?? "not_authenticated", "not authenticated", traceId);
  }

  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiError(
      response.status,
      problem?.error ?? "internal_error",
      problem?.message ?? response.statusText,
      problem?.traceId ?? traceId,
      problem?.retryable ?? null,
    );
  }

  if (response.status === 204) return undefined;

  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) return undefined;
  return (await response.json()) as T;
}

/** The same call for a route that always answers with a body. Saves every caller an `undefined` branch. */
export async function requestJson<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const body = await request<T>(path, options);
  if (body === undefined) {
    throw new ApiError(502, "internal_error", `no body from ${path}`, null);
  }
  return body;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> =>
    requestJson<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T | undefined> =>
    request<T>(path, { ...options, method: "POST", ...(body === undefined ? {} : { body }) }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T | undefined> =>
    request<T>(path, { ...options, method: "PATCH", ...(body === undefined ? {} : { body }) }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T | undefined> =>
    request<T>(path, { ...options, method: "PUT", ...(body === undefined ? {} : { body }) }),
  delete: <T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T | undefined> =>
    request<T>(path, { ...options, method: "DELETE" }),
} as const;

/* ---------------------------------------------------------------------------------------------------
 * The shapes this client actually reads. Transcribed from the route modules that produce them; where a
 * route returns more than the UI uses, only what is used is declared.
 * ------------------------------------------------------------------------------------------------- */

export type UserStatus = "pending" | "active" | "suspended";
/**
 * The delegated scope roles — `server/permissions/types.ts`'s `SCOPE_ROLES`, transcribed.
 *
 * `goal_author` arrived on 7 August 2026, because Goals borrowed `job_author` when it shipped and that
 * made "may write a changelog" and "may post a check-in to an executive OKR" one grant with no way to say
 * either alone. This is one of the two hand-written copies the server's own comment names; the other is
 * `pages/AccessGroupEditor.tsx`'s picker.
 */
export type ScopeRole = "bu_admin" | "job_author" | "goal_author";
/**
 * A work surface with its own address and its own role — `server/permissions/modules.ts`'s `APP_MODULES`.
 *
 * Administration is deliberately not one: it is a §3.6 section facet with a different combine rule, and
 * folding the two would put one answer behind two mechanisms.
 */
export type AppModule = "automations" | "goals";
export type AdminSection = "users" | "access" | "business_units" | "jobs" | "audit";
export type Cadence = "hourly" | "daily" | "weekly" | "fortnightly" | "monthly";
/**
 * What an update SAYS, as against where it says it — `ticket_jobs.kind`, transcribed from
 * `server/db/jobs.ts`'s `JOB_KINDS`.
 *
 * **Declared in the same release the server started sending it, and that timing is the whole point.**
 * `request<T>`'s generic is the caller's assertion about a response and is validated by nothing at
 * runtime (see this module's header, rule 3's neighbour) — so the two mistakes here are symmetrical and
 * both silent. Declare it EARLY and every `Job.kind` is `undefined` at runtime while the compiler swears
 * it is one of three strings: no branch throws, no request fails, and every screen that switches on it
 * takes whatever its unmatched branch happens to be, on every row at once. Declare it LATE and the client
 * cannot tell three kinds apart while the server distinguishes them, which is how a changelog's wording
 * ends up on a page that is not one.
 *
 * A closed union rather than `string`, because the two document kinds are not a longer changelog: one of
 * them can cause a commit into somebody's repository, and a form that could not tell them apart could not
 * warn about it.
 */
export type UpdateKind = "changelog" | "executive_summary" | "security_overview";
export type RunStatus = "running" | "succeeded" | "failed" | "skipped";
export type RunTrigger = "schedule" | "manual";

export interface SectionAccess {
  readonly view: boolean;
  readonly act: boolean;
}

export interface ScopeSummary {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
}

/** `GET /api/me` — everything the client needs to draw itself, and nothing it is trusted about. */
export interface Me {
  readonly user: {
    readonly id: string;
    readonly email: string;
    readonly displayName: string | null;
    readonly status: UserStatus;
    readonly isSuperuser: boolean;
  };
  readonly accessibleScopes: readonly ScopeSummary[];
  /** The unit this person is assigned to, if any. Explains a switcher with one entry; confers nothing. */
  readonly confinedTo: string | null;
  /** Whether this deployment requires an assignment — what "not assigned" means, which is not guessable. */
  readonly requiresBusinessUnit: boolean;
  readonly activeBu: string | null;
  readonly hasScopeAccess: boolean;
  readonly roles: readonly ScopeRole[];
  /**
   * Which work surfaces this subject may reach in the scope this answer was resolved for — **computed by
   * the server, and never by this client**.
   *
   * ⚠️ **Do not re-derive it from `roles`.** The rule lives once, in `server/permissions/modules.ts`, and
   * a client that owned a second copy of it would be a second decision path — of which the more permissive
   * always becomes the effective policy. Concretely: a navigation entry drawn for somebody every route in
   * the module refuses, or hidden from somebody they answer. `CLAUDE.md` records what a transcribed gate
   * costs — `canReachAdmin(me) || true` left 179 tests green — and a gate transcribed into the *client* is
   * the same mistake with a worse failure mode.
   *
   * Per SCOPE, like `roles`, and arriving with it: the bootstrap `/api/me` is scopeless and answers both
   * false unless exactly one unit is reachable. See `App.tsx`'s re-resolve and its pending state.
   */
  readonly modules: Readonly<Record<AppModule, boolean>>;
  readonly sections: Readonly<Record<AdminSection, SectionAccess>>;
  readonly auth: { readonly mode: "oidc" | "dev" };
  readonly csrfToken: string;
}

/** `GET /api/jobs` */
export interface Job {
  readonly id: string;
  readonly buId: string;
  readonly ownerId: string;
  /**
   * The subject this update's scheduled runs act as. `orphaned` means it cannot publish until a
   * business-unit administrator reassigns it — the person is gone or suspended.
   */
  readonly owner: {
    readonly id: string;
    readonly email: string | null;
    readonly status: UserStatus | null;
    readonly orphaned: boolean;
  };
  readonly name: string;
  /**
   * What this update says. Chosen once, when the update is created, and never afterwards.
   *
   * The immutability is the server's and is stated in three places there — `UpdateJobSchema` omits the
   * field by name, `UpdateJobPatch` has no slot for it, and a PATCH naming a kind that DISAGREES with the
   * stored row is refused in words. The reason is that a Confluence page is claimed by one update and its
   * whole body is regenerated from that update's own stored history at every publish, so a kind that
   * changed would leave a page whose past is bullets and whose future is a document, with nothing to
   * migrate the rows behind it. A screen that offered to edit this would be offering something the server
   * declines to do.
   */
  readonly kind: UpdateKind;
  readonly enabled: boolean;
  readonly cadence: Cadence;
  readonly timezone: string;
  readonly sendAtMinute: number;
  readonly sendOnWeekday: number | null;
  /**
   * Hours between runs, for the `hourly` cadence. 1 unless somebody chose otherwise, and every other
   * cadence ignores it. The floor of one is the product's; there is no maximum.
   */
  readonly intervalHours: number;
  readonly nextDueAt: string | null;
  readonly lastRunAt: string | null;
  /**
   * How far back this update reconstructs day sections the first time it sees a repository, in days.
   * 0 is off. An input control — how far back — never a word of the output.
   */
  readonly backfillDays: number;
  readonly postWhenEmpty: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface JobListResponse {
  readonly buId: string;
  readonly jobs: readonly Job[];
}

/** `GET /api/jobs/:id/runs` */
export interface Run {
  readonly id: string;
  readonly jobId: string;
  readonly status: RunStatus;
  readonly trigger: RunTrigger;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly counters: {
    readonly commitsSeen: number;
    /** Removed by the mechanical filter before any token was spent: bots, lockfiles, ignore lists. */
    readonly commitsDropped: number;
    /** Read by the model and judged not worth reporting. A DIFFERENT population from the above. */
    readonly commitsRoutine: number;
    readonly reposCovered: number;
    readonly reposFailed: number;
    readonly itemsPublished: number;
    readonly itemsTruncated: number;
    readonly truncated: boolean;
  };
  readonly error: string | null;
  readonly traceId: string | null;
  /** True when this run was written up for a day that had passed rather than published live on it. */
  readonly backfilled: boolean;
}

export interface RunListResponse {
  readonly jobId: string;
  readonly runs: readonly Run[];
}

/** `GET /api/admin/users` */
export interface AdminUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string | null;
  readonly status: UserStatus;
  /** The one business unit this person belongs to, or null for "not assigned". Never a grant. */
  readonly confinedTo: string | null;
  /**
   * Whether this address is in the deployment's `ADMIN_EMAILS`.
   *
   * A **display projection** of the predicate the resolver itself uses, computed per row on the server
   * and never stored. `06-modules.md` §1.5.3 requires a superuser to be marked distinctly from a granted
   * role, because it is a different kind of fact and removing every group would not remove it — and
   * without this field the roster's central columns tell three separate untruths about such an account.
   */
  readonly isSuperuser: boolean;
  readonly linked: boolean;
  readonly admittedAt: string | null;
  readonly lastSeenAt: string | null;
  readonly createdAt: string;
}

export interface AdminUserListResponse {
  readonly users: readonly AdminUser[];
  readonly total: number;
}

/** `GET /api/admin/audit` — the one scoped screen in the admin surface. */
export interface AuditEntry {
  readonly id: string;
  readonly buId: string;
  readonly at: string;
  readonly actorId: string | null;
  readonly actorEmail: string | null;
  readonly actorKind: string;
  readonly action: string;
  readonly subjectKind: string | null;
  readonly subjectId: string | null;
  readonly outcome: "allowed" | "refused" | "error";
  readonly detail: unknown;
  readonly traceId: string | null;
}

export interface AuditListResponse {
  readonly buId: string;
  readonly entries: readonly AuditEntry[];
  readonly total: number;
  readonly actions: readonly string[];
}

/* ---------------------------------------------------------------------------------------------------
 * Named calls. Thin, and worth having: the paths live in one file, so a route that moves is one edit.
 * ------------------------------------------------------------------------------------------------- */

/**
 * The session bootstrap.
 *
 * `buId` defaults to null — explicitly scopeless, because `/api/me` is what TELLS us which scopes exist,
 * and naming one before we know is how a first load ends in a refusal for a business unit the user never
 * chose.
 *
 * Naming a scope on a LATER call is not an optimisation, it is the only way to learn `roles`. The server
 * resolves per scope: `roles` is empty whenever the request did not name a business unit the subject can
 * reach, and it names one for the caller only when exactly one is reachable. So a subject with two units
 * has empty roles until asked again with a scope — and a UI that gated *Run now* or *New update* on the
 * first answer would hide them from every multi-unit author, permanently. `/api/me` is a GET, so the
 * explicit-BU guard on mutations does not apply.
 */
export function fetchMe(signal?: AbortSignal, buId: string | null = null): Promise<Me> {
  return api.get<Me>("/api/me", { buId, ...(signal ? { signal } : {}) });
}

export function fetchJobs(signal?: AbortSignal): Promise<JobListResponse> {
  return api.get<JobListResponse>("/api/jobs", signal ? { signal } : {});
}

export function fetchRuns(jobId: string, signal?: AbortSignal): Promise<RunListResponse> {
  return api.get<RunListResponse>(`/api/jobs/${encodeURIComponent(jobId)}/runs`, signal ? { signal } : {});
}

export function fetchAdminUsers(signal?: AbortSignal): Promise<AdminUserListResponse> {
  return api.get<AdminUserListResponse>("/api/admin/users", signal ? { signal } : {});
}

export function fetchAudit(signal?: AbortSignal): Promise<AuditListResponse> {
  return api.get<AuditListResponse>("/api/admin/audit", {
    query: { limit: 200 },
    ...(signal ? { signal } : {}),
  });
}

/** Sign out this browser. Instance-level on the server, so no business unit is named. */
export async function signOut(): Promise<void> {
  await api.post("/auth/logout", undefined, { buId: null });
}

/**
 * The development sign-in. Registered by the server ONLY when the authenticator is the development one,
 * which refuses to be constructed in production — this call simply 404s everywhere else.
 */
export async function devSignIn(email: string): Promise<void> {
  const response = await fetch("/auth/dev-login", {
    method: "POST",
    credentials: "same-origin",
    // The identity IS the header here; there is no body and no CSRF token to carry, since the request is
    // unauthenticated and the server passes those through untouched.
    headers: { "X-Dev-Email": email },
  });
  if (!response.ok) {
    const traceId = response.headers.get("x-c4s-trace-id");
    throw new ApiError(response.status, "not_authenticated", "development sign-in was refused", traceId);
  }
}

/* ---------------------------------------------------------------------------------------------------
 * Goals — registering an Atlassian objective, and checking in on its key results.
 *
 * Transcribed from `server/routes/goals.ts`'s `goalView` / `checkinView` / `itemView`, and from
 * `server/goals/refusals.ts` for the codes. Two things here are unlike the rest of this module and both
 * are deliberate; each is marked where it lives.
 * ------------------------------------------------------------------------------------------------- */

/** The three traffic-lights this connector can SET. Atlassian reports more, and we cannot drive them. */
export type GoalCheckinState = "on_track" | "at_risk" | "off_track";

/**
 * Transcribed from `server/db/goals.ts`'s `CHECKIN_ITEM_STATES`.
 *
 * ⚠️ `failed` and `indeterminate` are the two that must never be confused, and they are separate members
 * rather than one state with a flag for exactly that reason. `failed` means the gateway REFUSED, so
 * nothing landed and trying again is honest. `indeterminate` means nobody knows, so trying again may
 * leave two contradictory check-ins on a record with no delete — and it is **terminal**: the server
 * refuses to re-answer it (`answerCheckinItem`), refuses to post it (`markItemPosted`'s own UPDATE
 * carries `AND state <> 'indeterminate'`), and the post route refuses before the client is called.
 */
export type CheckinItemState = "pending" | "drafted" | "posted" | "skipped" | "failed" | "indeterminate";
export type CheckinStatus = "open" | "completed" | "abandoned";
export type SummarySource = "generated" | "user";

/**
 * The business unit's Atlassian connection — and `postedAs` is **not the signed-in person**.
 *
 * Every check-in is written with the unit's own credential, so Atlassian's history names a service
 * account while this application's audit trail names the human. `server/routes/goals.ts` carries
 * `postedAs` on every response precisely so a screen can say whose name will appear BEFORE anything is
 * sent; a screen that receives this and does not show it has dropped the only warning there is.
 */
export interface GoalConnection {
  readonly site: string;
  readonly postedAs: string;
}

export interface GoalView {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly url: string;
  /** Null where Atlassian did not return one — "not read", never an em dash (`06-modules.md` §5.3 R2). */
  readonly ownerName: string | null;
  /** When this snapshot was taken from Atlassian. §5.3 R1: a synchronised record states its last sync. */
  readonly syncedAt: string;
  readonly addedAt: string;
}

export interface CheckinView {
  readonly id: string;
  readonly status: CheckinStatus;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly actorEmail: string;
}

/** The number a key result tracks. `null` on the item means it tracks none — a fact, not a gap. */
export interface ItemMetric {
  readonly name: string | null;
  readonly type: string | null;
  readonly current: number | null;
  readonly start: number | null;
  readonly target: number | null;
}

export interface ItemView {
  readonly id: string;
  readonly position: number;
  readonly key: string;
  readonly name: string;
  readonly url: string | null;
  /** As Atlassian reports it — a WIDER vocabulary than `GoalCheckinState`, so deliberately a string. */
  readonly currentState: string | null;
  readonly metric: ItemMetric | null;
  readonly state: CheckinItemState;
  readonly newState: GoalCheckinState | null;
  readonly newScore: number | null;
  readonly newMetricValue: number | null;
  readonly note: string | null;
  readonly summary: string | null;
  readonly summarySource: SummarySource | null;
  readonly postedUpdateId: string | null;
  readonly postedAt: string | null;
  /** What Atlassian RECORDED, kept apart from what was intended. They disagree on a mis-set score. */
  readonly postedState: string | null;
  readonly postedScore: number | null;
  /**
   * The reading Atlassian accepted — which is NOT `newMetricValue`, the one that was asked for.
   *
   * `null` carries a third meaning here and the screen must not flatten it: the update landed, but its
   * reply said nothing about the metric. "Recorded 20" would be a claim nobody made.
   */
  readonly postedMetricValue: number | null;
  readonly error: string | null;
}

export type OpenCheckin = CheckinView & { readonly items: readonly ItemView[] };

/** `GET /api/goals` */
export interface GoalListResponse {
  readonly buId: string;
  /** Null rather than absent: "not connected" and "none added yet" are different screens. */
  readonly connection: GoalConnection | null;
  readonly goals: readonly GoalView[];
}

/** `GET /api/goals/:goalId/checkins` */
export interface GoalCheckinsResponse {
  readonly goal: GoalView;
  readonly connection: GoalConnection | null;
  readonly open: OpenCheckin | null;
  readonly history: readonly CheckinView[];
}

/** `POST /api/goals/:goalId/checkins` — opens one, or hands back the one already open. */
export interface StartCheckinResponse {
  readonly checkin: OpenCheckin;
  readonly resumed: boolean;
  readonly connection: GoalConnection;
}

/** `POST …/post`. `mismatch` is the read-back verdict and is invisible unless a screen renders it. */
export interface PostItemResponse {
  readonly item: ItemView;
  readonly mismatch: boolean;
  readonly url: string | null;
}

export interface DraftInput {
  readonly state: GoalCheckinState;
  /**
   * The confidence, as the API carries it: a whole number 1–100, already inside the state's band.
   *
   * ⚠️ **Omitting it is a real answer** — it means "nobody chose", and the server applies the band's
   * midpoint (`resolveScore`). That is precisely why its absence from the body assembly below was so
   * dangerous while it lasted: a dropped score is indistinguishable, on the wire and in the record, from a
   * person who left the field blank. The screen collects a decimal and converts it once; the conversion is
   * `readScoreDecimal`'s and nothing here divides or multiplies.
   */
  readonly score?: number;
  /** Omitted means "the reading holds where it was". Only sent where the key result has a metric. */
  readonly metricValue?: number;
  readonly note?: string;
  /** The person's own sentence, used verbatim. Absent means the model writes one. */
  readonly summary?: string;
}

/**
 * ⚠️ **The goals routes are the one place in this application where the server's `message` IS shown.**
 *
 * Everywhere else `problemFor` deliberately discards an error's message — an internal message is where a
 * table name or another tenant's identifier leaks — so `ApiError.userMessage` maps a code to a sentence
 * of ours. `server/goals/refusals.ts` inverts that: every refusal below is one the person can act on
 * themselves ("add a key result there, then start the check-in again"), the sentence is written for them,
 * and "The request was not valid." would send them hunting for a typo that is not there.
 *
 * The list is closed for the same reason `UPDATE_KIND_LABELS` is: an open `includes` over any code would
 * relay a message from a route that never promised one was safe to show.
 */
export const GOAL_REFUSAL_CODES = [
  "goals_not_connected",
  "goal_reference_unrecognised",
  "goal_not_visible",
  "goal_not_allowlisted",
  "goal_has_no_key_results",
  "checkin_closed",
  "checkin_already_posted",
  "checkin_not_drafted",
  "checkin_no_metric",
  // The band refusal names the state and the range it accepts ("At risk spans 0.4-0.6"). Omitted from
  // this list it becomes "The request was not valid.", which sends the writer hunting for a typo in a
  // number that is merely in the wrong band — the precise failure the list's own rationale describes.
  "checkin_score_out_of_band",
  "summarisation_not_configured",
  "checkin_refused",
  "checkin_indeterminate",
  // Closing a quarter. Every one of these says what was NOT written as well as what was refused —
  // "nothing has been created and nothing has been archived" is the clause that stops somebody going
  // to Atlassian to look for damage that is not there. Relayed generically they would say none of it.
  "closure_already_live",
  "closure_not_found",
  "closure_wrong_state",
  "closure_target_unrecognised",
  "closure_target_not_visible",
  "closure_target_not_allowlisted",
  "closure_target_is_source",
  "closure_quarter_tag_unknown",
] as const;

export type GoalRefusalCode = (typeof GOAL_REFUSAL_CODES)[number];

export function isGoalRefusalCode(code: string): code is GoalRefusalCode {
  return (GOAL_REFUSAL_CODES as readonly string[]).includes(code);
}

/**
 * The sentence to put in front of a person: the server's own words for a goals refusal, ours otherwise.
 *
 * The empty-message guard is not defensive clutter — `readProblem` yields `""` for a body without one,
 * and a refusal rendered as an empty paragraph is a screen that has told the reader nothing at all.
 */
export function goalErrorMessage(error: ApiError): string {
  return isGoalRefusalCode(error.code) && error.message !== "" ? error.message : error.userMessage;
}

function goalPath(goalId: string): string {
  return `/api/goals/${encodeURIComponent(goalId)}`;
}

function itemPath(goalId: string, checkinId: string, itemId: string): string {
  return `${goalPath(goalId)}/checkins/${encodeURIComponent(checkinId)}/items/${encodeURIComponent(itemId)}`;
}

export function fetchGoals(signal?: AbortSignal): Promise<GoalListResponse> {
  return api.get<GoalListResponse>("/api/goals", signal ? { signal } : {});
}

/** `reference` is whatever was pasted — a URL, a bare key, or an ARI. The server does the parsing. */
export async function registerGoal(reference: string): Promise<GoalView> {
  const body = await requestJson<{ readonly goal: GoalView }>("/api/goals", {
    method: "POST",
    body: { reference },
  });
  return body.goal;
}

export async function removeGoal(goalId: string): Promise<void> {
  await api.delete(goalPath(goalId));
}

// -----------------------------------------------------------------------------------------------
// Closing a quarter.
//
// ⚠️ FIVE CALLS, AND ONLY `submitClosure` / `resumeClosure` CHANGE ANYTHING IN ATLASSIAN. Opening
// reads, `previewClosureTarget` resolves and proposes, abandoning deletes a row that caused nothing
// to happen. A screen may call the other three as freely as it likes; the two that write are behind
// a confirmation and are never retried automatically. See `server/goals/close-quarter.ts`.
// -----------------------------------------------------------------------------------------------

export type ClosureState = "open" | "carrying" | "carried" | "archiving" | "closed" | "halted";
export type CarryState = "not_selected" | "pending" | "created" | "carried" | "failed" | "indeterminate";
export type ArchiveState = "pending" | "archived" | "failed" | "indeterminate";
export type QuarterTagSource = "objective" | "model" | "derived" | "person";

export interface ClosureView {
  readonly id: string;
  readonly goalId: string;
  readonly state: ClosureState;
  readonly openedAt: string;
  readonly targetGoalKey: string | null;
  readonly targetGoalName: string | null;
  readonly quarterTag: string | null;
  readonly quarterTagSource: QuarterTagSource | null;
  readonly targetDate: string | null;
  readonly targetDateConfidence: string | null;
  readonly haltedReason: string | null;
  readonly sourceArchivedAt: string | null;
  readonly closedAt: string | null;
}

export interface ClosureMetricView {
  readonly name: string;
  readonly type: string | null;
  readonly current: number | null;
  readonly start: number | null;
  readonly target: number | null;
}

export interface ClosureItemView {
  readonly id: string;
  readonly keyResultKey: string;
  readonly name: string;
  readonly url: string | null;
  readonly currentState: string | null;
  readonly tags: readonly string[];
  /** Display names, not ARIs — the ARI is the server's business and a person cannot read one. */
  readonly teams: readonly string[];
  readonly metric: ClosureMetricView | null;
  readonly carryOver: boolean;
  readonly carryState: CarryState;
  readonly carryFailure: string | null;
  /**
   * The key of the goal this application actually created, once it has. What lets the screen say
   * "carried over as TROPI8-701" rather than "done" — after a halt, that difference is the difference
   * between knowing what exists in Atlassian and having to go and look.
   */
  readonly createdGoalKey: string | null;
  readonly archiveState: ArchiveState;
  readonly archivedAt: string | null;
  readonly archiveFailure: string | null;
}

export interface ClosureResponse {
  readonly closure: ClosureView;
  readonly items: readonly ClosureItemView[];
  readonly resumed?: boolean;
  readonly objective?: GoalView;
  readonly connection?: GoalConnection | null;
}

/** `POST …/closures/:id/target` — resolution and a proposed quarter tag, with NOTHING written. */
export interface ClosureTargetPreview {
  readonly target: { readonly key: string; readonly ari: string; readonly name: string; readonly url: string | null };
  readonly quarterTag: string | null;
  readonly quarterTagSource: QuarterTagSource | null;
  readonly modelWasAsked: boolean;
  readonly suggestedTargetDate: string | null;
  readonly preview: readonly { readonly itemId: string; readonly tags: readonly string[] }[];
}

/**
 * The answer to a submit or a resume.
 *
 * ⚠️ `outcome: "halted"` ARRIVES AS A 200, not as an `ApiError`. Real goals were created and the ledger
 * describing them is this body; a client that treated it as a failed request would invite a person to
 * run it again, which is the one thing that duplicates a quarter. Render `items` and offer *Resume*.
 */
export interface ClosureRunResponse {
  readonly closure: ClosureView;
  readonly items: readonly ClosureItemView[];
  readonly outcome: "closed" | "halted";
  readonly notice: string | null;
  readonly reason: string | null;
}

export interface ClosureSubmitInput {
  readonly reference: string;
  readonly carryOverItemIds: readonly string[];
  readonly quarterTag?: string;
  readonly targetDate?: string;
  readonly targetDateConfidence?: "DAY" | "MONTH" | "QUARTER";
}

function closurePath(goalId: string, closureId: string): string {
  return `${goalPath(goalId)}/closures/${encodeURIComponent(closureId)}`;
}

/** Opens one, or hands back the one already open — the server refuses to have two. */
export function startClosure(goalId: string): Promise<ClosureResponse> {
  return requestJson<ClosureResponse>(`${goalPath(goalId)}/closures`, { method: "POST", body: {} });
}

export function fetchClosure(
  goalId: string,
  closureId: string,
  signal?: AbortSignal,
): Promise<ClosureResponse> {
  return api.get<ClosureResponse>(closurePath(goalId, closureId), signal ? { signal } : {});
}

export function previewClosureTarget(
  goalId: string,
  closureId: string,
  reference: string,
  quarterTag?: string,
): Promise<ClosureTargetPreview> {
  return requestJson<ClosureTargetPreview>(`${closurePath(goalId, closureId)}/target`, {
    method: "POST",
    body: { reference, ...(quarterTag === undefined || quarterTag === "" ? {} : { quarterTag }) },
  });
}

export function submitClosure(
  goalId: string,
  closureId: string,
  input: ClosureSubmitInput,
): Promise<ClosureRunResponse> {
  return requestJson<ClosureRunResponse>(`${closurePath(goalId, closureId)}/submit`, {
    method: "POST",
    body: {
      reference: input.reference,
      carryOverItemIds: [...input.carryOverItemIds],
      ...(input.quarterTag === undefined || input.quarterTag === "" ? {} : { quarterTag: input.quarterTag }),
      ...(input.targetDate === undefined || input.targetDate === "" ? {} : { targetDate: input.targetDate }),
      ...(input.targetDateConfidence === undefined ? {} : { targetDateConfidence: input.targetDateConfidence }),
    },
  });
}

export function resumeClosure(goalId: string, closureId: string): Promise<ClosureRunResponse> {
  return requestJson<ClosureRunResponse>(`${closurePath(goalId, closureId)}/resume`, {
    method: "POST",
    body: {},
  });
}

export async function abandonClosure(goalId: string, closureId: string): Promise<void> {
  await api.delete(closurePath(goalId, closureId));
}

export function fetchGoalCheckins(goalId: string, signal?: AbortSignal): Promise<GoalCheckinsResponse> {
  return api.get<GoalCheckinsResponse>(`${goalPath(goalId)}/checkins`, signal ? { signal } : {});
}

export function startCheckin(goalId: string): Promise<StartCheckinResponse> {
  return requestJson<StartCheckinResponse>(`${goalPath(goalId)}/checkins`, { method: "POST" });
}

/**
 * Record the answers and have the sentence written. **Writes nothing to Atlassian** — that is `postItem`,
 * and the two are separate calls so that nothing can be sent which a person has not seen.
 */
export async function draftCheckinItem(
  goalId: string,
  checkinId: string,
  itemId: string,
  input: DraftInput,
): Promise<ItemView> {
  const body = await requestJson<{ readonly item: ItemView }>(
    `${itemPath(goalId, checkinId, itemId)}/draft`,
    {
      method: "POST",
      // Built up conditionally rather than spread whole: `exactOptionalPropertyTypes` is on, and an
      // explicit `metricValue: undefined` on a key result with no metric is a `checkin_no_metric` refusal.
      //
      // ⚠️ **Every field the screen collects must be named here, and the cost of forgetting one is
      // silence.** A field absent from this list never leaves the browser: the request is well formed, the
      // route is happy, the check-in posts, and the only sign is a record holding a value the person did
      // not choose. `score` shipped missing from exactly this list and was caught by the assertion in
      // `pages/GoalCheckin.test.tsx` that mounts the screen with no ports and reads the request body —
      // which is the only kind of test that can see a field being dropped between a form and a seam.
      body: {
        state: input.state,
        ...(input.score === undefined ? {} : { score: input.score }),
        ...(input.metricValue === undefined ? {} : { metricValue: input.metricValue }),
        ...(input.note === undefined ? {} : { note: input.note }),
        ...(input.summary === undefined ? {} : { summary: input.summary }),
      },
    },
  );
  return body.item;
}

export function postCheckinItem(
  goalId: string,
  checkinId: string,
  itemId: string,
): Promise<PostItemResponse> {
  return requestJson<PostItemResponse>(`${itemPath(goalId, checkinId, itemId)}/post`, { method: "POST" });
}

export async function skipCheckinItem(
  goalId: string,
  checkinId: string,
  itemId: string,
): Promise<ItemView> {
  const body = await requestJson<{ readonly item: ItemView }>(
    `${itemPath(goalId, checkinId, itemId)}/skip`,
    { method: "POST" },
  );
  return body.item;
}

export async function closeCheckin(
  goalId: string,
  checkinId: string,
  status: "completed" | "abandoned",
): Promise<CheckinView> {
  const body = await requestJson<{ readonly checkin: CheckinView }>(
    `${goalPath(goalId)}/checkins/${encodeURIComponent(checkinId)}/close`,
    { method: "POST", body: { status } },
  );
  return body.checkin;
}
